import { useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, CircleCheck, KeyRound, ShieldCheck, Smartphone, User } from 'lucide-react';
import { DISPLAY_NAME_MAX, PASSWORD_MIN } from '@cp/shared';
import {
  Banner,
  Button,
  Dialog,
  Facts,
  Fact,
  Field,
  Input,
  PasswordInput,
  TabPanel,
  Tabs,
} from '@/components/ui';
import { AuthenticatorSetup } from '@/components/account/AuthenticatorSetup';
import { useActivateMfa, useChangePassword, useStartMfaSetup, useUpdateProfile } from '@/hooks/useAuth';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import { roleList } from '@/utils/labels';
import type { CurrentUser } from '@/types/api';

/**
 * Account settings.
 *
 * A dialog rather than a page: there are two short forms here, and a route of
 * their own would mean leaving whatever the operator was looking at to change
 * their own name.
 */

type Tab = 'profile' | 'security' | 'mfa';

export function AccountDialog({
  open,
  user,
  onClose,
}: {
  readonly open: boolean;
  readonly user: CurrentUser;
  readonly onClose: () => void;
}): ReactNode {
  const [tab, setTab] = useState<Tab>('profile');

  return (
    <Dialog open={open} title="Учётная запись" description={user.login} onClose={onClose}>
      <div className="stack">
        <Tabs
          label="Разделы учётной записи"
          active={tab}
          onChange={setTab}
          tabs={[
            { id: 'profile', label: 'Профиль', icon: User },
            { id: 'security', label: 'Пароль', icon: KeyRound },
            { id: 'mfa', label: 'Второй фактор', icon: ShieldCheck },
          ]}
        />

        <TabPanel id="profile" active={tab}>
          <ProfileForm user={user} />
        </TabPanel>

        <TabPanel id="security" active={tab}>
          <PasswordForm />
        </TabPanel>

        <TabPanel id="mfa" active={tab}>
          <MfaPanel enabled={user.mfaEnabled} />
        </TabPanel>
      </div>
    </Dialog>
  );
}

function ProfileForm({ user }: { readonly user: CurrentUser }): ReactNode {
  const [name, setName] = useState(user.displayName ?? '');

  const save = useUpdateProfile({ onSuccess: () => toast.success('Имя сохранено') });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    save.mutate(name.trim() === '' ? null : name.trim());
  };

  const unchanged = name.trim() === (user.displayName ?? '');

  return (
    <form className="stack" onSubmit={submit}>
      <Field
        label="Отображаемое имя"
        htmlFor="profile-name"
        hint="Отображается в журнале. Пусто — используется логин"
      >
        <Input
          id="profile-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={DISPLAY_NAME_MAX}
          placeholder={user.login}
          autoComplete="name"
        />
      </Field>

      <Facts>
        <Fact label="Логин">
          <span className="mono">{user.login}</span>
        </Fact>
        <Fact label="Почта">{user.email ?? '—'}</Fact>
        <Fact label="Роли">{roleList(user.roles)}</Fact>
      </Facts>

      <p className="field-hint">
        Логин, почту и роли изменяет администратор.
      </p>

      {save.isError ? (
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(save.error, 'Не удалось сохранить имя')}
        </p>
      ) : null}

      <div className="row-between">
        <span />
        <Button type="submit" variant="primary" busy={save.isPending} disabled={unchanged}>
          Сохранить
        </Button>
      </div>
    </form>
  );
}

function PasswordForm(): ReactNode {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const change = useChangePassword({
    onSuccess: (result) => {
      setCurrent('');
      setNext('');
      setRepeat('');
      toast.success(
        'Пароль изменён',
        result.revokedSessions > 0
          ? `Другие сессии завершены: ${result.revokedSessions}`
          : 'Других активных сессий не было',
      );
    },
  });

  // Checked here as well as on the server, because the server cannot tell the
  // difference between "mistyped twice" and "mistyped once" — it only ever
  // receives one of the two values.
  const mismatch = repeat.length > 0 && next !== repeat;
  const tooShort = next.length > 0 && next.length < PASSWORD_MIN;
  const valid = current.length > 0 && next.length >= PASSWORD_MIN && next === repeat;
  const wrongCurrent = change.isError && errorField(change.error) === 'currentPassword';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (valid && !change.isPending) change.mutate({ currentPassword: current, newPassword: next });
  };

  return (
    <form className="stack" onSubmit={submit}>
      {/* The server names the field it rejected, so the message goes beside
          that field rather than in a banner the operator has to match up. */}
      <Field
        label="Текущий пароль"
        htmlFor="password-current"
        {...(wrongCurrent ? { error: 'Текущий пароль не подошёл' } : {})}
      >
        <PasswordInput
          id="password-current"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          autoComplete="current-password"
          invalid={wrongCurrent}
          required
        />
      </Field>

      <Field
        label="Новый пароль"
        htmlFor="password-new"
        {...(tooShort ? { error: `Не короче ${PASSWORD_MIN} символов` } : { hint: `Не короче ${PASSWORD_MIN} символов` })}
      >
        <PasswordInput
          id="password-new"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          autoComplete="new-password"
          invalid={tooShort}
          required
        />
      </Field>

      <Field
        label="Повторите новый пароль"
        htmlFor="password-repeat"
        {...(mismatch ? { error: 'Пароли не совпадают' } : {})}
      >
        <PasswordInput
          id="password-repeat"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          autoComplete="new-password"
          invalid={mismatch}
          required
        />
      </Field>

      <p className="field-hint row">
        <ShieldCheck size={13} aria-hidden="true" />
        Остальные сессии будут завершены.
      </p>

      {change.isError && !wrongCurrent ? (
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(change.error, 'Не удалось сменить пароль')}
        </p>
      ) : null}

      {change.isSuccess ? (
        <p className="field-hint row icon-healthy">
          <CircleCheck size={14} aria-hidden="true" />
          Пароль изменён
        </p>
      ) : null}

      <div className="row-between">
        <span />
        <Button type="submit" variant="primary" busy={change.isPending} disabled={!valid}>
          Сменить пароль
        </Button>
      </div>
    </form>
  );
}

/**
 * The second factor, from the account's own side.
 *
 * Binding is self-service. Re-binding is not: a live session able to swap the
 * device would turn a stolen session into a stolen account, so a lost phone goes
 * through an administrator.
 */
function MfaPanel({ enabled }: { readonly enabled: boolean }): ReactNode {
  const setup = useStartMfaSetup();
  const activate = useActivateMfa({
    onSuccess: () => {
      toast.success('Приложение привязано', 'Следующий вход потребует код');
      setup.reset();
    },
  });

  if (enabled) {
    return (
      <div className="stack">
        <p className="row-tight">
          <CircleCheck size={16} aria-hidden="true" className="icon-healthy" />
          <span className="strong">Приложение-аутентификатор привязано</span>
        </p>
        <p className="muted">
          Для смены устройства обратитесь к администратору.
        </p>
      </div>
    );
  }

  if (setup.data) {
    const activateError = !activate.isError
      ? null
      : errorField(activate.error) === 'token'
        ? 'Срок привязки истёк. Начните заново.'
        : errorField(activate.error) === 'code'
          ? 'Код не подошёл'
          : errorText(activate.error, 'Не удалось привязать приложение');

    return (
      <AuthenticatorSetup
        material={setup.data}
        busy={activate.isPending}
        error={activateError}
        submitLabel="Привязать"
        onSubmit={(code) => activate.mutate({ token: setup.data.token, code })}
      />
    );
  }

  return (
    <div className="stack">
      <Banner tone="info" icon={Smartphone}>
        Обязателен для владельцев и администраторов.
      </Banner>

      {setup.isError ? (
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(setup.error, 'Не удалось начать привязку')}
        </p>
      ) : null}

      <div className="row-between">
        <span />
        <Button variant="primary" icon={ShieldCheck} busy={setup.isPending} onClick={() => setup.mutate()}>
          Привязать приложение
        </Button>
      </div>
    </div>
  );
}
